import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, Lock, BarChart3, Settings2, Megaphone, 
  Check, X, Sliders, ExternalLink, ShieldAlert, ArrowLeft,
  ChevronRight, RefreshCw, FileText, Info, HelpCircle, CheckCircle2
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { CookieConsentService, CookiePreferences, COOKIE_INVENTORY, CookieItem } from '../services/cookieConsentService';
import { SponsoredService } from '../services/sponsoredService';

export const CookieConsentPage: React.FC = () => {
  const tokens = useThemeTokens();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const [preferences, setPreferences] = useState<CookiePreferences>(() => CookieConsentService.getPreferences());
  const [activeTab, setActiveTab] = useState<'all' | 'necessary' | 'analytics' | 'functional' | 'marketing'>('all');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const handleConfigUpdate = () => setConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleConfigUpdate);

    const handleSync = (e: any) => {
      setPreferences(CookieConsentService.getPreferences());
    };
    window.addEventListener('cookie-consent-granular-updated', handleSync);
    window.addEventListener('cookie-consent-updated', handleSync);

    return () => {
      window.removeEventListener('sponsored-config-updated', handleConfigUpdate);
      window.removeEventListener('cookie-consent-granular-updated', handleSync);
      window.removeEventListener('cookie-consent-updated', handleSync);
    };
  }, []);

  const handleToggle = (cat: 'analytics' | 'functional' | 'marketing') => {
    setPreferences(prev => {
      const nextVal = !prev[cat];
      return {
        ...prev,
        [cat]: nextVal,
        doNotSell: cat === 'marketing' ? !nextVal : prev.doNotSell
      };
    });
  };

  const handleToggleDoNotSell = () => {
    setPreferences(prev => {
      const nextDoNotSell = !prev.doNotSell;
      return {
        ...prev,
        doNotSell: nextDoNotSell,
        marketing: nextDoNotSell ? false : prev.marketing
      };
    });
  };

  const handleSave = () => {
    const updated = CookieConsentService.savePreferences({
      status: 'customized',
      analytics: preferences.analytics,
      functional: preferences.functional,
      marketing: preferences.marketing,
      doNotSell: preferences.doNotSell
    });
    setPreferences(updated);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3500);
  };

  const handleAcceptAll = () => {
    const updated = CookieConsentService.acceptAll();
    setPreferences(updated);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3500);
  };

  const handleRejectAll = () => {
    const updated = CookieConsentService.rejectNonEssential();
    setPreferences(updated);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3500);
  };

  const filteredCookies = COOKIE_INVENTORY.filter(c => activeTab === 'all' || c.category === activeTab);

  const formattedDate = preferences.updatedAt 
    ? new Date(preferences.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Not yet recorded';

  return (
    <div className="space-y-10 sm:space-y-12 pb-24 max-w-5xl mx-auto text-slate-900 dark:text-slate-100">
      
      {/* Top Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-xs font-bold text-slate-500 border-b border-slate-200 dark:border-slate-800 pb-4">
        <a href="/gateway" className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center gap-1">
          <ArrowLeft size={14} /> Home
        </a>
        <ChevronRight size={14} className="text-slate-300 dark:text-slate-700" />
        <span>Compliance & Governance</span>
        <ChevronRight size={14} className="text-slate-300 dark:text-slate-700" />
        <span className="text-slate-900 dark:text-slate-100 font-extrabold">Cookie Policy & Consent Center</span>
      </div>

      {/* Main Hero Header */}
      <header className={`p-8 sm:p-10 rounded-3xl border shadow-sm relative overflow-hidden transition-all duration-300 ${tokens.cardBg} ${tokens.accentBorder}`}>
        <div className="relative z-10 space-y-4 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${tokens.badge}`}>
              Global Compliance Active
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-bold">
              GDPR · ePrivacy · CCPA / CPRA
            </span>
          </div>

          <h1 className={`text-2xl sm:text-4xl font-black tracking-tight leading-tight ${tokens.headerFont}`}>
            Cookie Policy & Privacy Preferences
          </h1>

          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
            At Diamond School of Nursing, your trust and privacy are paramount. We utilize cookies and browser storage technologies to maintain secure student authentication, measure exam rendering speeds, remember your state testing syllabus, and respect your privacy choices under global data protection laws.
          </p>

          <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 dark:text-slate-400 pt-2">
            <span>Last Updated: October 2026</span>
            <span>·</span>
            <span>Version {preferences.version}</span>
            <span>·</span>
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
              <ShieldCheck size={14} /> Status: {preferences.status.toUpperCase()}
            </span>
          </div>
        </div>
      </header>

      {/* Interactive Preference Center Card */}
      <section className={`p-6 sm:p-8 rounded-3xl border shadow-md space-y-6 transition-all duration-300 ${tokens.cardBg} ${tokens.accentBorder}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
          <div className="space-y-1">
            <h2 className={`text-xl font-black tracking-tight ${tokens.headerFont} flex items-center gap-2`}>
              <Sliders size={20} className={tokens.accentText} />
              Interactive Consent & Preferences Manager
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Adjust your permissions below. Your choices take effect immediately across all study modules.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleRejectAll}
              className={`px-4 py-2.5 ${tokens.secondaryBtn} rounded-xl font-bold text-xs transition-all cursor-pointer`}
            >
              Reject Non-Essential
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className={`px-5 py-2.5 ${tokens.primaryBtn} rounded-xl font-bold text-xs transition-all cursor-pointer shadow-sm`}
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Success Alert Banner */}
        {saveSuccess && (
          <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 text-emerald-800 dark:text-emerald-200 animate-pop">
            <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div className="text-xs font-bold">
              Preferences successfully saved and synchronized. Your active consent timestamp is recorded as {formattedDate}.
            </div>
          </div>
        )}

        {/* California CCPA Notice & Toggle */}
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-bold text-sm text-amber-900 dark:text-amber-200">
              <ShieldAlert size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <span>California Consumer Privacy Act (CCPA / CPRA) Opt-Out</span>
            </div>
            <p className="text-xs text-amber-800 dark:text-amber-300/90 leading-relaxed max-w-2xl">
              <strong>"Do Not Sell or Share My Personal Information"</strong>: Toggle this switch to immediately prevent the sharing of your personal data with third-party advertisers and clinical partner networks.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
              {preferences.doNotSell ? 'Opted Out' : 'Allow Sharing'}
            </span>
            <button
              type="button"
              onClick={handleToggleDoNotSell}
              role="switch"
              aria-checked={preferences.doNotSell}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.doNotSell ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-700'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.doNotSell ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* 4 Category Rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Category 1: Strictly Necessary */}
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <Lock size={16} />
                </div>
                <span className="px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 rounded-md">
                  Always Active
                </span>
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                1. Strictly Necessary Cookies
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Essential for core website operation. Powers student login sessions, CSRF token security, exam progress saving, and this consent configuration. Cannot be deactivated.
              </p>
            </div>
            <div className="pt-2 text-[10px] font-mono text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
              wix_auth_token, dns_session_token, cna_cookie_consent
            </div>
          </div>

          {/* Category 2: Performance & Analytics */}
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                  <BarChart3 size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('analytics')}
                  role="switch"
                  aria-checked={preferences.analytics}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.analytics ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                2. Performance & Diagnostic Analytics
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Helps us monitor Study Hub server uptime, identify quiz loading bottlenecks, and evaluate student completion rates across clinical domains via Google Analytics 4.
              </p>
            </div>
            <div className="pt-2 text-[10px] font-mono text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
              _ga, _ga_*, cna_speed_metric
            </div>
          </div>

          {/* Category 3: Functional & Learning Personalization */}
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                  <Settings2 size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('functional')}
                  role="switch"
                  aria-checked={preferences.functional}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.functional ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.functional ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                3. Functional & Learning Personalization
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Remembers your selected state jurisdiction (e.g., California, Texas, Florida), font-size accessibility adjustments, audio lecture speed, and chosen interface theme.
              </p>
            </div>
            <div className="pt-2 text-[10px] font-mono text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
              dns_selected_state, data-theme, audio_playback_rate
            </div>
          </div>

          {/* Category 4: Marketing & Advertising */}
          <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                  <Megaphone size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('marketing')}
                  role="switch"
                  aria-checked={preferences.marketing}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.marketing ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.marketing ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                4. Advertising & Educational Partners
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Enables personalized nursing career notices, antimicrobial medical scrub discounts, and authorized healthcare educational promotions via Google AdSense and Meta Pixel.
              </p>
            </div>
            <div className="pt-2 text-[10px] font-mono text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
              _fbp, __gads, __gpi
            </div>
          </div>

        </div>

        {/* Action Button Strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Current consent timestamp: <span className="font-semibold text-slate-800 dark:text-slate-200">{formattedDate}</span>
          </p>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleSave}
              className={`w-full sm:w-auto px-8 py-3.5 ${tokens.primaryBtn} rounded-2xl font-black text-xs uppercase tracking-widest shadow-md hover:shadow-lg transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2`}
            >
              <Check size={16} />
              <span>Save My Preferences</span>
            </button>
          </div>
        </div>

      </section>

      {/* Complete Technical Cookie Inventory Table */}
      <section className={`p-6 sm:p-8 rounded-3xl border shadow-sm space-y-6 transition-all duration-300 ${tokens.cardBg} ${tokens.accentBorder}`}>
        <div className="space-y-1">
          <h2 className={`text-xl font-black tracking-tight ${tokens.headerFont} flex items-center gap-2`}>
            <FileText size={20} className={tokens.accentText} />
            Complete Technical Cookie Inventory
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
            Published in accordance with the European Union ePrivacy Directive (Directive 2002/58/EC) and US State Disclosure Mandates.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-850 rounded-2xl">
          {(['all', 'necessary', 'analytics', 'functional', 'marketing'] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all capitalize cursor-pointer ${
                activeTab === tab ? tokens.activeTab : tokens.inactiveTab
              }`}
            >
              {tab === 'all' ? 'All Cookies' : tab}
            </button>
          ))}
        </div>

        {/* Responsive Table */}
        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100/80 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase font-black tracking-wider text-slate-500 dark:text-slate-400">
                <th className="p-3.5">Cookie Identifier</th>
                <th className="p-3.5">Category</th>
                <th className="p-3.5">Provider / Host</th>
                <th className="p-3.5">Purpose & Utility</th>
                <th className="p-3.5">Retention Period</th>
                <th className="p-3.5">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-150 dark:divide-slate-850">
              {filteredCookies.map(c => (
                <tr key={c.name} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                  <td className="p-3.5 font-mono font-bold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                    {c.name}
                  </td>
                  <td className="p-3.5">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider capitalize ${
                      c.category === 'necessary' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' :
                      c.category === 'analytics' ? 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300' :
                      c.category === 'functional' ? 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300' :
                      'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                    }`}>
                      {c.category}
                    </span>
                  </td>
                  <td className="p-3.5 text-slate-600 dark:text-slate-300 font-medium">
                    {c.provider}
                  </td>
                  <td className="p-3.5 text-slate-600 dark:text-slate-300 leading-relaxed max-w-sm font-medium">
                    {c.purpose}
                  </td>
                  <td className="p-3.5 text-slate-500 dark:text-slate-400 font-semibold whitespace-nowrap">
                    {c.duration}
                  </td>
                  <td className="p-3.5 text-slate-500 dark:text-slate-400 font-semibold whitespace-nowrap">
                    {c.type}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Comprehensive Compliance Explanations */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* GDPR & European Rights */}
        <div className={`p-6 sm:p-7 rounded-3xl border shadow-xs space-y-3 ${tokens.cardBg} ${tokens.accentBorder}`}>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className={tokens.accentText} />
            <h3 className={`text-base font-bold ${tokens.headerFont}`}>
              European Union & UK Privacy Rights (GDPR)
            </h3>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
            Under Articles 6 and 7 of the General Data Protection Regulation (GDPR), Diamond School of Nursing requires your freely given, specific, and affirmative consent before setting any non-essential cookies. You have the right to withdraw your consent at any time without penalizing your clinical training or test access.
          </p>
          <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 list-disc pl-4 font-medium">
            <li>Right to access personal data collected via telemetry.</li>
            <li>Right to erasure ("Right to be Forgotten").</li>
            <li>Right to restrict and object to automated processing.</li>
          </ul>
        </div>

        {/* California Rights (CCPA / CPRA) */}
        <div className={`p-6 sm:p-7 rounded-3xl border shadow-xs space-y-3 ${tokens.cardBg} ${tokens.accentBorder}`}>
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-500" />
            <h3 className={`text-base font-bold ${tokens.headerFont}`}>
              California Consumer Rights (CCPA / CPRA)
            </h3>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
            Under California Civil Code § 1798.100 et seq., California residents have the right to know the categories of personal data collected, request deletion, and opt out of the "sale" or "sharing" of personal data for cross-context behavioral advertising.
          </p>
          <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 list-disc pl-4 font-medium">
            <li>Notice at Collection of personal information.</li>
            <li>Explicit opt-out mechanism via our CCPA switch above.</li>
            <li>Non-discrimination for exercising privacy choices.</li>
          </ul>
        </div>

        {/* Browser-Level Controls */}
        <div className={`p-6 sm:p-7 rounded-3xl border shadow-xs space-y-3 ${tokens.cardBg} ${tokens.accentBorder}`}>
          <div className="flex items-center gap-2">
            <Settings2 size={18} className={tokens.accentText} />
            <h3 className={`text-base font-bold ${tokens.headerFont}`}>
              Direct Browser Management
            </h3>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
            Most web browsers allow you to manage cookie preferences directly in their settings. You can set your browser to refuse all cookies or notify you when a cookie is placed. Please note that disabling strictly necessary cookies will prevent login to the study hub.
          </p>
          <div className="flex flex-wrap gap-2 pt-1 text-[11px] font-bold">
            <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300">Chrome: chrome://settings/cookies</span>
            <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300">Safari: Settings &gt; Privacy</span>
            <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300">Firefox: about:preferences#privacy</span>
          </div>
        </div>

        {/* Contact Compliance */}
        <div className={`p-6 sm:p-7 rounded-3xl border shadow-xs space-y-3 ${tokens.cardBg} ${tokens.accentBorder}`}>
          <div className="flex items-center gap-2">
            <Info size={18} className={tokens.accentText} />
            <h3 className={`text-base font-bold ${tokens.headerFont}`}>
              Contact Compliance & Data Protection
            </h3>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
            If you have questions regarding our cookie practices, data retention, or would like to submit a formal data deletion or access request, please contact our Institutional Data Protection Office:
          </p>
          <div className="text-xs space-y-1 font-semibold text-slate-700 dark:text-slate-300 pt-1">
            <p>Email: <a href="mailto:compliance@diamondnursingschool.com" className={`${tokens.accentText} hover:underline`}>compliance@diamondnursingschool.com</a></p>
            <p>Institution: Diamond School of Nursing</p>
            <p>Address: State Board Approved Healthcare Training Facility</p>
          </div>
        </div>

      </section>

    </div>
  );
};
